import { useState } from 'react';
import styled from 'styled-components';
import { createReservation } from '../../api/reservations';


const 초기폼 = {
    summary: '',
    description: '',
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    startDateTime: '',
    endDateTime: '',
};

const ReservationModal = ({ storeUuid, channelUuid, onClose, onSuccess }) => {
    const [폼, set폼] = useState(초기폼);
    const [저장중, set저장중] = useState(false);
    const [에러, set에러] = useState(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        set폼((prev) => ({ ...prev, [name]: value }));
    };

    const toISOWithSeoul = (localDateTimeStr) => {
        if (!localDateTimeStr) return '';
        return new Date(localDateTimeStr).toLocaleString('sv-SE', {
            timeZone: 'Asia/Seoul',
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
        }).replace(' ', 'T') + '+09:00';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        set에러(null);
        set저장중(true);

        const body = {
            summary: 폼.summary,
            description: 폼.description || undefined,
            customer: {
                name: 폼.customerName,
                phone: 폼.customerPhone || undefined,
                email: 폼.customerEmail || undefined,
            },
            schedule: {
                start: { date_time: toISOWithSeoul(폼.startDateTime), time_zone: 'Asia/Seoul' },
                end:   { date_time: toISOWithSeoul(폼.endDateTime),   time_zone: 'Asia/Seoul' },
            },
            status: 'CONFIRMED',
        };

        try {
            await createReservation(storeUuid, channelUuid, body);
            onSuccess?.();
            onClose();
        } catch {
            set에러('예약 저장 중 오류가 발생했습니다.');
        } finally {
            set저장중(false);
        }
    };

    return (
        <Overlay onClick={onClose}>
            <ModalCard onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                    <ModalTitle>예약 추가</ModalTitle>
                    <CloseButton onClick={onClose}>✕</CloseButton>
                </ModalHeader>

                <Form onSubmit={handleSubmit}>
                    <Section>
                        <SectionTitle>예약 정보</SectionTitle>
                        <Field>
                            <Label>제목 *</Label>
                            <Input
                                name="summary"
                                placeholder="예: 헤어 커트 및 염색"
                                value={폼.summary}
                                onChange={handleChange}
                                required
                            />
                        </Field>
                        <Field>
                            <Label>메모</Label>
                            <Textarea
                                name="description"
                                placeholder="추가 메모 (선택)"
                                value={폼.description}
                                onChange={handleChange}
                                rows={3}
                            />
                        </Field>
                    </Section>

                    <Section>
                        <SectionTitle>고객 정보</SectionTitle>
                        <Field>
                            <Label>이름 *</Label>
                            <Input
                                name="customerName"
                                placeholder="홍길동"
                                value={폼.customerName}
                                onChange={handleChange}
                                required
                            />
                        </Field>
                        <FieldRow>
                            <Field>
                                <Label>연락처</Label>
                                <Input
                                    name="customerPhone"
                                    placeholder="010-0000-0000"
                                    value={폼.customerPhone}
                                    onChange={handleChange}
                                />
                            </Field>
                            <Field>
                                <Label>이메일</Label>
                                <Input
                                    name="customerEmail"
                                    type="email"
                                    placeholder="example@email.com"
                                    value={폼.customerEmail}
                                    onChange={handleChange}
                                />
                            </Field>
                        </FieldRow>
                    </Section>

                    <Section>
                        <SectionTitle>일정</SectionTitle>
                        <FieldRow>
                            <Field>
                                <Label>시작 *</Label>
                                <Input
                                    name="startDateTime"
                                    type="datetime-local"
                                    value={폼.startDateTime}
                                    onChange={handleChange}
                                    required
                                />
                            </Field>
                            <Field>
                                <Label>종료 *</Label>
                                <Input
                                    name="endDateTime"
                                    type="datetime-local"
                                    value={폼.endDateTime}
                                    onChange={handleChange}
                                    required
                                />
                            </Field>
                        </FieldRow>
                    </Section>

                    {에러 && <ErrorText>{에러}</ErrorText>}

                    <ButtonRow>
                        <CancelButton type="button" onClick={onClose}>
                            취소
                        </CancelButton>
                        <SubmitButton type="submit" disabled={저장중}>
                            {저장중 ? '저장 중...' : '예약 저장'}
                        </SubmitButton>
                    </ButtonRow>
                </Form>
            </ModalCard>
        </Overlay>
    );
};


const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 24px;
    box-sizing: border-box;
`;

const ModalCard = styled.div`
    width: 100%;
    max-width: 520px;
    background: #ffffff;
    border: 1px solid #eeeeee;
    border-radius: 16px;
    padding: 32px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    max-height: 90vh;
    overflow-y: auto;
`;

const ModalHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const ModalTitle = styled.h2`
    font-family: Pretendard;
    font-size: 18px;
    font-weight: 700;
    color: #111111;
    margin: 0;
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    font-size: 16px;
    color: #aaaaaa;
    cursor: pointer;
    padding: 4px;
    line-height: 1;

    &:hover {
        color: #333333;
    }
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const SectionTitle = styled.span`
    font-family: Pretendard;
    font-size: 11px;
    font-weight: 600;
    color: #aaaaaa;
    text-transform: uppercase;
    letter-spacing: 0.08em;
`;

const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
`;

const FieldRow = styled.div`
    display: flex;
    gap: 12px;
`;

const Label = styled.label`
    font-family: Pretendard;
    font-size: 12px;
    font-weight: 500;
    color: #777777;
`;

const Input = styled.input`
    background: #f7f8fa;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    padding: 10px 12px;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 400;
    color: #111111;
    outline: none;
    width: 100%;
    box-sizing: border-box;

    &::placeholder {
        color: #c0c0c0;
    }

    &:focus {
        border-color: #487BFD;
    }
`;

const Textarea = styled.textarea`
    background: #f7f8fa;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    padding: 10px 12px;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 400;
    color: #111111;
    outline: none;
    width: 100%;
    box-sizing: border-box;
    resize: vertical;

    &::placeholder {
        color: #c0c0c0;
    }

    &:focus {
        border-color: #487BFD;
    }
`;

const ErrorText = styled.span`
    font-family: Pretendard;
    font-size: 13px;
    color: #EF4444;
`;

const ButtonRow = styled.div`
    display: flex;
    gap: 8px;
    justify-content: flex-end;
`;

const CancelButton = styled.button`
    background: none;
    border: 1px solid #e5e5e5;
    border-radius: 8px;
    padding: 10px 20px;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 500;
    color: #777777;
    cursor: pointer;
    transition: border-color 0.15s, color 0.15s;

    &:hover {
        border-color: #bbbbbb;
        color: #333333;
    }
`;

const SubmitButton = styled.button`
    background: #487BFD;
    border: none;
    border-radius: 8px;
    padding: 10px 24px;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 600;
    color: #ffffff;
    cursor: pointer;
    transition: opacity 0.15s;

    &:hover:not(:disabled) {
        opacity: 0.85;
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }
`;


export default ReservationModal;
